import Swal from 'sweetalert2';
import api from '../../utils/axios';
import { useState, useCallback } from 'react';

interface EmpresaItem {
  Id: number;
  Nombre: string;
  NIT: string;
  Direccion: string;
  RepresentanteLegal: string;
  Logo?: string;
  Activo: boolean;
  Eliminado: boolean;
}

interface EmpresaDto {
  Nombre: string;
  NIT: string;
  Direccion: string;
  RepresentanteLegal: string;
  Logo?: string;
  Activo: boolean;
  Eliminado: boolean;
}

interface SoftDeleteResponse {
  success: boolean;
  empresa: string;
  sucursalesAfectadas?: {
    Id: number;
    Nombre: string;
  }[];
  message: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message: string;
}

const useEmpresasApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = '/administracion/empresas';

  const getAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.post<EmpresaItem[]>(`${baseUrl}/findAll`);
      return response.data.filter(item => !item.Eliminado);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: EmpresaDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/create`, {
        ...data,
        Eliminado: false
      });
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (id: number, data: EmpresaDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (err) {
      const error = err as ApiError;
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const response = await api.post<SoftDeleteResponse>(`${baseUrl}/softDelete/${id}`);
      
      if (response.data.sucursalesAfectadas && response.data.sucursalesAfectadas.length > 0) {
        const sucursalesList = response.data.sucursalesAfectadas
          .map(sucursal => `<li>${sucursal.Nombre}</li>`)
          .join('');

        const confirmResult = await Swal.fire({
          title: 'Atención',
          html: `
            <p>Esta empresa tiene las siguientes sucursales activas:</p>
            <ul>${sucursalesList}</ul>
            <p>Si continúa, estas sucursales serán desactivadas.</p>
            <p>¿Desea continuar con la eliminación?</p>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, eliminar',
          cancelButtonText: 'Cancelar'
        });

        if (!confirmResult.isConfirmed) {
          return false;
        }

        const deleteResponse = await api.post<SoftDeleteResponse>(`${baseUrl}/softDelete/${id}/confirm`);

        await Swal.fire({
          title: 'Operación completada',
          html: `
            <p>Las siguientes sucursales han sido desactivadas:</p>
            <ul>${sucursalesList}</ul>
            <p>${deleteResponse.data.message}</p>
          `,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        
        return true;
      } else {
        await Swal.fire({
          title: 'Éxito',
          text: response.data.message,
          icon: 'success',
          confirmButtonText: 'OK'
        });
        return true;
      }
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    getAll,
    create,
    update,
    remove
  };
};

export { useEmpresasApi };
export type { EmpresaItem, EmpresaDto, ApiError };